import React, { useState } from 'react';
import { aboutChristinaData } from '../data/mock';
import { Award, Quote, X, Eye, GraduationCap, Users, ShieldCheck, TrendingUp, BookOpen } from 'lucide-react';

// Certificate/Flyer data
const certificatesData = [
  {
    id: 'leadership',
    title: 'Leadership Basics',
    subtitle: 'Professional Training Course',
    audience: 'Asset Managers, Executive Leadership, Regional Managers, Community Directors and Maintenance Supervisors',
    duration: '60 minutes',
    description: 'Thought-provoking and practical, this course motivates your teams while keeping daily operations on track.',
    content: [
      'Introduction: Defining Leadership',
      'Supportive Leadership Role',
      'Core Leadership Responsibilities',
      'Team Engagement & Development',
      'Flexible Leadership & Service'
    ],
    format: 'In person preferred. Can be combined with other courses or as a series of monthly events.',
    location: 'Customer to provide',
    icon: GraduationCap,
    type: 'course'
  },
  {
    id: 'accountability',
    title: 'Accountability',
    subtitle: 'Professional Training Course',
    audience: 'Asset Managers, Executive Leadership, Regional Managers, Community Director, and Maintenance Supervisor',
    duration: '60 minutes',
    description: 'A focused look at what accountability really means to a team — and how to apply it in ways that drive results.',
    content: [
      'Team Member Behavior',
      'Accountability',
      'Lack of Accountability',
      'Applying Accountability',
      'Planning'
    ],
    format: 'In person preferred. Can be combined with other courses or as a series of monthly events.',
    location: 'Provided by customer',
    icon: Users,
    type: 'course'
  },
  {
    id: 'risk-mitigation',
    title: 'Risk Mitigation',
    subtitle: 'Professional Training Course',
    audience: 'Asset Managers, Executive Leadership, Regional Managers, Community Director, and Maintenance Supervisor',
    duration: '60 minutes',
    description: 'Equip your team with the tools to identify, document, and manage risk. Includes real-world scenarios and examples.',
    content: [
      'What is Risk Mitigation?',
      'Types of Risk',
      'Incident Reports',
      'Supporting Information',
      'Managing Documents',
      'Inspections',
      'Other Risks'
    ],
    format: 'In person preferred. Can be combined with other courses or as a series of monthly events.',
    location: 'Provided by customer',
    icon: ShieldCheck,
    type: 'course'
  },
  {
    id: 'asset-manager',
    title: 'How to Think Like an Asset Manager',
    subtitle: 'Professional Training Course',
    audience: 'Asset Managers, Executive Leadership, and Regional Managers',
    duration: '60 minutes',
    description: 'Built for leaders who want to understand the investor perspective. Learn to present your expertise with confidence.',
    content: [
      'Asset Management Defined',
      'Management Defined',
      'Ownership Types',
      'Supporting the Asset Manager',
      'Calculating Returns and Value',
      'How to Be Prepared',
      'Presenting Your Expertise',
      'Relationship Management'
    ],
    format: 'In person preferred. Can be combined with other courses or as a series of monthly events.',
    location: 'Provided by customer',
    icon: TrendingUp,
    type: 'course'
  },
  {
    id: 'course-menu',
    title: 'Course Menu 2026',
    subtitle: 'Full Training Catalog',
    audience: 'Asset Managers, Executive Leadership, Regional Managers, Community Directors and Maintenance Supervisors',
    duration: 'Varies by course',
    description: 'Browse our complete catalog of currently available courses and upcoming offerings.',
    content: [
      'Leadership Basics',
      'Accountability',
      'Risk Mitigation',
      'How to Think Like an Asset Manager'
    ],
    comingSoon: [
      'How to Analyze Your Market Position',
      'How Do I Write a SWOT?',
      'Building Bench Strength',
      'Conflict Resolution',
      'Change Management',
      'Time Management'
    ],
    format: 'In-person preferred. Can be combined as a series of monthly events.',
    location: 'Provided by customer',
    icon: BookOpen,
    type: 'menu'
  },
  {
    id: 'change-management',
    title: 'Change Management',
    subtitle: 'Cornell University Certificate',
    institution: 'Cornell University - eCornell',
    year: '2022',
    description: 'Professional certification in Change Management from Cornell University, demonstrating expertise in leading organizational change initiatives.',
    pdfUrl: 'https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/fqaa5xy3_Certificate%20of%20Change%20Management%20%281%29.pdf',
    icon: Award,
    type: 'certificate'
  }
];

const CertificateCard = ({ certificate, onView }) => {
  const Icon = certificate.icon;
  
  const handleClick = () => {
    if (certificate.type === 'certificate' && certificate.pdfUrl) {
      // Open PDF in new tab
      window.open(certificate.pdfUrl, '_blank');
    } else {
      onView(certificate);
    }
  };
  
  return (
    <div className="relative bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden group hover:shadow-xl transition-shadow duration-300">
      {/* Logo watermark */}
      <div className="absolute top-4 right-4 w-16 h-16 opacity-20">
        <img 
          src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/shtdt606_C%20Clark%20logo.jpg" 
          alt="CC Logo"
          className="w-full h-full object-contain"
        />
      </div>
      
      {/* Header band */}
      <div className={`px-6 py-4 ${certificate.type === 'certificate' ? 'bg-gradient-to-r from-[#b31b1b] to-[#d44444]' : 'bg-gradient-to-r from-[#6b2d7b] to-[#8b3d9b]'}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-white font-bold text-lg">{certificate.title}</h4>
            <p className="text-white/80 text-sm">{certificate.subtitle}</p>
          </div>
        </div>
      </div>
      
      {/* Partial content preview */}
      <div className="px-6 py-4 h-32 overflow-hidden relative">
        <p className="text-slate-600 text-sm mb-3">{certificate.description}</p>
        {certificate.duration && (
          <p className="text-xs text-slate-500">
            <span className="font-semibold text-[#6b2d7b]">Duration:</span> {certificate.duration}
          </p>
        )}
        {certificate.institution && (
          <p className="text-xs text-slate-500">
            <span className="font-semibold text-[#b31b1b]">Institution:</span> {certificate.institution}
          </p>
        )}
        {certificate.year && (
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-semibold text-[#b31b1b]">Year:</span> {certificate.year}
          </p>
        )}
        
        {/* Fade overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </div>
      
      {/* View button */}
      <div className="px-6 pb-4">
        <button
          onClick={handleClick}
          className={`w-full flex items-center justify-center gap-2 py-3 font-semibold rounded-xl transition-colors ${
            certificate.type === 'certificate' 
              ? 'bg-[#b31b1b] hover:bg-[#9a1717] text-white' 
              : 'bg-[#7ed321] hover:bg-[#6bc11a] text-[#2d1b38]'
          }`}
        >
          <Eye className="w-4 h-4" />
          {certificate.type === 'certificate' ? 'View Certificate' : 'View Details'}
        </button>
      </div>
    </div>
  );
};

const CertificateModal = ({ certificate, onClose }) => {
  if (!certificate) return null;
  const Icon = certificate.icon;
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center z-10 transition-colors"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>
        
        {/* Logo in corner */}
        <div className="absolute top-6 left-6 w-20 h-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/shtdt606_C%20Clark%20logo.jpg" 
            alt="Christina Clark Consulting Logo"
            className="w-full h-full object-contain"
          />
        </div>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#6b2d7b] to-[#8b3d9b] px-8 pt-8 pb-6 text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">{certificate.title}</h3>
          <p className="text-white/80 text-lg">{certificate.subtitle}</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
            <span className="text-white/90 text-sm">Course Duration: {certificate.duration}</span>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Description */}
          <div>
            <p className="text-slate-700 text-lg leading-relaxed">{certificate.description}</p>
          </div>
          
          {/* Audience */}
          <div className="bg-slate-50 rounded-xl p-5">
            <h4 className="text-[#6b2d7b] font-semibold mb-2">Suggested Audience</h4>
            <p className="text-slate-600">{certificate.audience}</p>
          </div>
          
          {/* Course Content */}
          {certificate.content && (
            <div>
              <h4 className="text-[#6b2d7b] font-semibold mb-3">
                {certificate.type === 'menu' ? 'Currently Available Courses' : 'Course Content'}
              </h4>
              <ul className="space-y-2">
                {certificate.content.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <div className="w-6 h-6 rounded-full bg-[#7ed321]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#6b2d7b] text-xs font-bold">{index + 1}</span>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Coming Soon Section for Course Menu */}
          {certificate.comingSoon && (
            <div className="bg-[#6b2d7b]/5 rounded-xl p-5">
              <h4 className="text-[#6b2d7b] font-semibold mb-3">Coming Soon</h4>
              <ul className="space-y-2">
                {certificate.comingSoon.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-500">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-slate-400 text-xs font-bold">•</span>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Location */}
          {certificate.location && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500">Location</p>
              <p className="text-slate-700 font-medium">{certificate.location}</p>
            </div>
          )}
          
          {/* Format */}
          <div className="flex items-center gap-3 p-4 bg-[#7ed321]/10 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-[#7ed321]/30 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-[#6b2d7b]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Format</p>
              <p className="text-[#6b2d7b] font-semibold">{certificate.format}</p>
            </div>
          </div>
          
          {/* Footer with logo */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/shtdt606_C%20Clark%20logo.jpg" 
                alt="CC Logo"
                className="w-12 h-12 object-contain"
              />
              <div>
                <p className="text-[#6b2d7b] font-semibold">Christina Clark Consulting</p>
                <p className="text-slate-500 text-sm">Strategic Operations & Training Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutChristina = () => {
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  
  return (
    <section id="about" className="py-24 bg-purple-200/50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#7ed321]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-[#6b2d7b]/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Column */}
          <div className="relative flex justify-center">
            {/* Main Image with Elegant Frame */}
            <div className="relative z-10 max-w-sm">
              {/* Outer decorative frame */}
              <div className="absolute -inset-3 bg-gradient-to-br from-[#7ed321] via-[#6bc11a] to-[#7ed321] rounded-2xl transform rotate-2 opacity-80" />
              <div className="absolute -inset-3 bg-gradient-to-tr from-[#6b2d7b]/40 via-[#7ed321]/60 to-[#6b2d7b]/40 rounded-2xl transform -rotate-1 opacity-60" />
              
              {/* Inner frame with border */}
              <div className="relative p-3 bg-white rounded-xl shadow-2xl shadow-[#6b2d7b]/20">
                <div className="overflow-hidden rounded-lg border-4 border-[#6b2d7b]/10 shadow-inner">
                  <img
                    src={aboutChristinaData.image}
                    alt={aboutChristinaData.name}
                    className="w-full h-auto object-cover"
                  />
                </div>
                
                {/* Name plate at bottom of frame */}
                <div className="mt-4 mb-1 text-center py-2 bg-gradient-to-r from-[#6b2d7b]/5 via-white to-[#6b2d7b]/5 rounded-lg">
                  <p className="text-[#6b2d7b] font-semibold text-sm tracking-wide">
                    {aboutChristinaData.name}
                  </p>
                  <p className="text-slate-500 text-xs mt-0.5">
                    CPM® | 40+ Years Experience
                  </p>
                </div>
              </div>
              
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#6b2d7b] rounded-tl-lg z-20" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#6b2d7b] rounded-tr-lg z-20" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#6b2d7b] rounded-bl-lg z-20" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#6b2d7b] rounded-br-lg z-20" />
            </div>
          </div>
          
          {/* Quote Card - Below image on mobile */}
          <div className="lg:hidden mt-8 mx-auto max-w-sm">
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-100">
              <Quote className="w-6 h-6 text-[#7ed321] mb-2" />
              <p className="text-slate-600 italic leading-relaxed text-sm">
                "{aboutChristinaData.quote}"
              </p>
            </div>
          </div>

          {/* Content Column */}
          <div className="space-y-8">
            <div>
              <p className="text-[#7ed321] font-semibold mb-2">Meet the Expert</p>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#6b2d7b] mb-2">
                {aboutChristinaData.name}
              </h2>
              <p className="text-xl text-slate-600">
                {aboutChristinaData.title}
              </p>
            </div>

            <p className="text-lg text-slate-600 leading-relaxed">
              {aboutChristinaData.bio}
            </p>

            {/* Credentials */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-[#6b2d7b] flex items-center gap-2">
                <Award className="w-5 h-5 text-[#7ed321]" />
                Credentials & Achievements
              </h4>
              <ul className="space-y-3">
                {aboutChristinaData.credentials.map((credential, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#7ed321] mt-2 flex-shrink-0" />
                    <span>{credential}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Quote Card - Desktop only */}
            <div className="hidden lg:block bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <Quote className="w-8 h-8 text-[#7ed321] mb-3" />
              <p className="text-slate-700 italic leading-relaxed text-lg">
                "{aboutChristinaData.quote}"
              </p>
            </div>
          </div>
        </div>
        
        {/* Training Certificates Section */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <p className="text-[#7ed321] font-semibold mb-2">Professional Training & Credentials</p>
            <h3 className="text-3xl lg:text-4xl font-bold text-[#6b2d7b]">
              Training Courses & Certifications
            </h3>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {certificatesData.map((cert) => (
              <CertificateCard 
                key={cert.id} 
                certificate={cert} 
                onView={setSelectedCertificate}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Certificate Modal */}
      <CertificateModal 
        certificate={selectedCertificate} 
        onClose={() => setSelectedCertificate(null)} 
      />
    </section>
  );
};

export default AboutChristina;
